import { Schema, Stream, Effect, Queue } from 'effect';

// src/index.ts
var Platform = Schema.Literals(["mac", "other"]);
var Shortcut = Schema.Struct({
  key: Schema.String,
  modifiers: Schema.Array(Schema.Literals(["Mod", "Control", "Alt", "Shift", "Meta"]))
});
var Binding = Schema.Struct({
  id: Schema.String,
  label: Schema.String,
  shortcut: Shortcut,
  scope: Schema.optionalKey(Schema.String),
  priority: Schema.optionalKey(Schema.Number),
  isDisabled: Schema.optionalKey(Schema.Boolean),
  allowEditable: Schema.optionalKey(Schema.Boolean),
  preventDefault: Schema.optionalKey(Schema.Boolean),
  repeat: Schema.optionalKey(Schema.Boolean)
});
var aliases = {
  esc: "Escape",
  space: "Space",
  " ": "Space",
  return: "Enter",
  enter: "Enter",
  escape: "Escape",
  tab: "Tab",
  home: "Home",
  end: "End",
  backspace: "Backspace",
  delete: "Delete",
  pageup: "PageUp",
  pagedown: "PageDown",
  arrowup: "ArrowUp",
  arrowdown: "ArrowDown",
  arrowleft: "ArrowLeft",
  arrowright: "ArrowRight",
  plus: "+",
  del: "Delete",
  up: "ArrowUp",
  down: "ArrowDown",
  left: "ArrowLeft",
  right: "ArrowRight"
};
var normalizeKey = (key) => aliases[key.toLowerCase()] ?? (key.length === 1 ? key.toLowerCase() : /^f\d{1,2}$/i.test(key) ? key.toUpperCase() : key);
var shortcut = (key, ...modifiers2) => ({ key: normalizeKey(key), modifiers: [...new Set(modifiers2)] });
var modifiers = (value, platform) => {
  const values = new Set(
    value.modifiers.map(
      (mod) => mod === "Mod" ? platform === "mac" ? "Meta" : "Control" : mod
    )
  );
  return ["Control", "Alt", "Shift", "Meta"].filter(
    (mod) => values.has(mod)
  );
};
var aria = (value, platform) => [
  ...modifiers(value, platform),
  normalizeKey(value.key) === "+" ? "plus" : normalizeKey(value.key)
].join("+");
var display = (value, platform) => {
  const labels = platform === "mac" ? { Control: "\u2303", Alt: "\u2325", Shift: "\u21E7", Meta: "\u2318" } : { Control: "Ctrl", Meta: "Win", Alt: "Alt", Shift: "Shift" };
  const key = normalizeKey(value.key);
  return [
    ...modifiers(value, platform).map((mod) => labels[mod]),
    key.length === 1 ? key.toUpperCase() : key
  ].join(platform === "mac" ? "" : "+");
};
var resolve = (bindings, event, platform, scopes = [], editable = false) => {
  if (event.isComposing || event.defaultPrevented) return;
  return bindings.map((binding, index) => ({
    binding,
    index,
    depth: binding.scope === void 0 ? scopes.length : scopes.indexOf(binding.scope)
  })).filter(
    ({ binding: b, depth }) => depth >= 0 && !b.isDisabled && (!editable || b.allowEditable) && (!event.repeat || b.repeat) && normalizeKey(event.key) === normalizeKey(b.shortcut.key) && [event.ctrlKey, event.altKey, event.shiftKey, event.metaKey].every(
      (pressed, i) => pressed === modifiers(b.shortcut, platform).includes(
        ["Control", "Alt", "Shift", "Meta"][i]
      )
    )
  ).sort(
    (a, b) => a.depth - b.depth || (b.binding.priority ?? 0) - (a.binding.priority ?? 0) || a.index - b.index
  )[0]?.binding;
};
var conflicts = (bindings, platform) => bindings.flatMap(
  (a, i) => bindings.slice(i + 1).filter(
    (b) => !a.isDisabled && !b.isDisabled && a.scope === b.scope && aria(a.shortcut, platform) === aria(b.shortcut, platform)
  ).map((b) => [a.id, b.id])
);
var events = (bindings, platform, target) => Stream.callback(
  (queue) => Effect.gen(function* () {
    const doc = target ?? (typeof document === "undefined" ? void 0 : document);
    if (!doc) return;
    yield* Effect.acquireRelease(
      Effect.sync(() => {
        const listener = (event) => {
          const path = event.composedPath().filter(
            (node) => node instanceof doc.defaultView.HTMLElement
          );
          const scopes = path.map((node) => node.id).filter(Boolean);
          const editable = path.some(
            (node) => node.matches("input,textarea,select") || node.isContentEditable
          );
          const binding = resolve(
            typeof bindings === "function" ? bindings() : bindings,
            event,
            platform,
            scopes,
            editable
          );
          if (!binding) return;
          if (binding.preventDefault !== false) event.preventDefault();
          Queue.offerUnsafe(queue, binding.id);
        };
        doc.addEventListener("keydown", listener);
        return () => doc.removeEventListener("keydown", listener);
      }),
      (dispose) => Effect.sync(dispose)
    );
    return yield* Effect.never;
  })
);

export { Binding, Platform, Shortcut, aria, conflicts, display, events, modifiers, normalizeKey, resolve, shortcut };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map